<template>
  <div :class="cn(feedVariants({ variant }), props.class)">
    <FeedItem
      v-for="item in processedFeed"
      :key="item.id"
      :variant="variant"
      :item="item"
      :task="task"
      :lineSize="lineSize"
      :last="last(processedFeed) === item"
      @preview="$emit('preview', $event)"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import { last, isNull } from "lodash";
import { FeedItem, FeedTypes, feedVariants } from "@/SharedComponents/Feed";
import { cn } from "@/Utils/cn";

defineEmits(["preview"]);

const props = defineProps({
  variant: { type: String, required: true },
  items: { type: Array, required: true },
  task: { type: Object, default: () => {} },
  reverse: { type: Boolean, default: false },
});

const lineSize = computed(() =>
  [FeedTypes.TIME_LOG, FeedTypes.MATERIAL_LOG].includes(props.variant)
    ? "sm"
    : "md"
);

const feed = computed(() =>
  props.items.filter((item) =>
    props.variant === FeedTypes.ACTIVITY_LOG
      ? isNull(item.comment)
      : !isNull(item.comment)
  )
);

// Processes and groups checklist items in the feed
const processedFeed = computed(() => {
  const result = [];
  const feedItems = [...feed.value];
  const processedIndices = new Set();

  // Finds neighbouring checklist items within the time threshold
  const getNeighbours = (startIndex, timestamp, direction) => {
    const items = [];
    let currentIndex = startIndex + direction;

    while (
      (direction > 0 ? currentIndex < feedItems.length : currentIndex >= 0) &&
      feedItems[currentIndex]?.description === "checklist_item_created" &&
      Math.abs(
        new Date(feedItems[currentIndex].created_at).getTime() - timestamp
      ) <= 2000 &&
      !processedIndices.has(currentIndex)
    ) {
      items.push(feedItems[currentIndex]);
      processedIndices.add(currentIndex);
      currentIndex += direction;
    }

    return items;
  };

  // Processes a checklist item and its neighbours
  const processChecklistItem = (index, isCreatedWithItems = false) => {
    const currentItem = feedItems[index];
    const timestamp = new Date(currentItem.created_at).getTime();

    const checklistItems = isCreatedWithItems ? [] : [currentItem];

    if (!isCreatedWithItems) {
      processedIndices.add(index);
    }

    checklistItems.push(...getNeighbours(index, timestamp, 1));
    checklistItems.push(...getNeighbours(index, timestamp, -1));

    if (checklistItems.length > (isCreatedWithItems ? 0 : 1)) {
      result.push({
        ...currentItem,
        checklistItems,
        isGrouped: true,
        groupType: isCreatedWithItems ? "created_with_items" : "items_only",
      });
      if (isCreatedWithItems) {
        processedIndices.add(index);
      }
    } else if (!isCreatedWithItems) {
      result.push(currentItem);
    }
  };

  // Process checklist_created items first
  feedItems.forEach((item, index) => {
    if (processedIndices.has(index)) return;
    if (item.description === "checklist_created") {
      processChecklistItem(index, true);
    }
  });

  // Process checklist_item_created and other items
  feedItems.forEach((item, index) => {
    if (processedIndices.has(index)) return;

    if (item.description === "checklist_item_created") {
      processChecklistItem(index);
    } else {
      result.push(item);
      processedIndices.add(index);
    }
  });

  const sortedResult = result.sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
  return props.reverse ? sortedResult.reverse() : sortedResult;
});
</script>
